import { POSITION_ALL_KEY } from '@/components/partials/jobs/JobsBackOffice/JobsBackOfficeFilters/constants';
import type {
  FilterOption,
  FiltersConfig,
  FilterTransformator,
  FilterTransformatorMultiselect,
  FilterValues,
  GetQueryFuncPayload,
  GQLAttributes,
  InputFilter,
  MultiselectFilter,
  MultiselectFilterSet,
  QueryAttributes,
  QueryParams,
  RangeFilter,
  SingleselectFilter,
  TabsFilter,
  ToggleFilter,
} from '@/components/partials/jobs/JobsBackOffice/JobsBackOfficeFilters/types';
import { toCamel } from '@/components/utils/core/word';

export const filterKeyToHuman = (value: string): string =>
  value
    .split('_')
    .map(chunk => `${chunk.charAt(0).toUpperCase()}${chunk.slice(1).toLocaleLowerCase()}`)
    .join(' ');

export const keyToFilterOptionMapper = (key: string): FilterOption => ({
  key,
  title: filterKeyToHuman(key),
});

export const filterKeyToHumanHyphenated = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((chunk, index) => {
      const firstChar = chunk.charAt(0);
      return `${index === 0 ? firstChar.toUpperCase() : firstChar}${chunk.slice(1)}`;
    })
    .join('-');

export const setSelectedMapper = (selectedItems?: (string | null)[]) => (filterOption: FilterOption) => {
  const selected = !selectedItems || selectedItems.map(String).includes(String(filterOption.key));

  return { ...filterOption, selected };
};

export const pickSelectedFieldMapper = (filterOption: FilterOption) => filterOption.selected;

const getFilterQueryParamValue = (items: FilterOption[]) =>
  items.reduce((acc, item) => `${acc}${acc ? ',' : ''}${item.key}`, '');

const getFilterValuesFromParam = (valuesString: string): string[] => valuesString.split(',');

export const getFiltersQuery = (filters: FilterOption[], activeFiltersParam: string | null) => {
  if (!activeFiltersParam) {
    return getFilterQueryParamValue(filters);
  }

  const activeFilters = getFilterValuesFromParam(activeFiltersParam);

  const filtered = filters.filter((filterOption: FilterOption) => activeFilters.includes(String(filterOption.key)));

  return getFilterQueryParamValue(filtered);
};

const multiselectTransformator: FilterTransformatorMultiselect = {
  toGQLQuery: (filterValue, filterConfig) => {
    if (filterConfig.toGQLQuery) {
      return filterConfig.toGQLQuery(filterValue, filterConfig);
    }

    const selected = filterValue.filter(pickSelectedFieldMapper);

    if (selected.length === filterValue.length) {
      return undefined;
    }

    if (!selected.length) {
      return undefined;
    }

    return selected.map(filter => filter.key);
  },
  toURLParam: filterValue => {
    const selected = filterValue.filter(pickSelectedFieldMapper);

    if (selected.length === filterValue.length) {
      return null;
    }

    return getFiltersQuery(selected, null);
  },
  toFilterOptions(filterValueFromQuery, defaultFilterValue) {
    if (!filterValueFromQuery) {
      return defaultFilterValue;
    }

    const activeFilters = getFilterValuesFromParam(filterValueFromQuery);
    return defaultFilterValue.map(setSelectedMapper(activeFilters));
  },
};

export const filterTransformator: FilterTransformator = {
  multiselect: multiselectTransformator,
  singleselect: {
    toGQLQuery(filterValue) {
      const [selectedOption] = filterValue.filter(pickSelectedFieldMapper);

      return selectedOption.key;
    },
    toURLParam(filterValue, filterConfig) {
      const selected = filterValue.find(pickSelectedFieldMapper);
      const selectedDefault = filterConfig.options.find(pickSelectedFieldMapper);

      // case when default key is not empty
      if (selectedDefault?.key !== POSITION_ALL_KEY && selectedDefault?.key === selected?.key) return selected?.key;

      if (selected?.key === selectedDefault?.key) return undefined;

      return selected?.key;
    },
    toFilterOptions(filterValueFromQuery, defaultFilterValue) {
      if (!filterValueFromQuery) {
        return defaultFilterValue;
      }

      return defaultFilterValue.map(setSelectedMapper([filterValueFromQuery]));
    },
  },
  tabs: {
    toGQLQuery(filterValue) {
      const [selectedOption] = filterValue.filter(pickSelectedFieldMapper);

      return selectedOption.key;
    },
    toURLParam(filterValue, filterConfig) {
      const selected = filterValue.find(pickSelectedFieldMapper);
      const selectedDefault = filterConfig.options.find(pickSelectedFieldMapper);

      // case when default key is not empty
      if (selectedDefault?.key !== POSITION_ALL_KEY && selectedDefault?.key === selected?.key) return selected?.key;

      if (selected?.key === selectedDefault?.key) return undefined;

      return selected?.key;
    },
    toFilterOptions(filterValueFromQuery, defaultFilterValue) {
      if (!filterValueFromQuery) {
        return defaultFilterValue;
      }

      return defaultFilterValue.map(setSelectedMapper([filterValueFromQuery]));
    },
  },
  input: {
    toGQLQuery(filterValue) {
      if (!filterValue) return undefined;

      return filterValue;
    },
    toURLParam(filterValue) {
      return filterValue;
    },
    toFilterOptions(filterValue) {
      return filterValue ?? '';
    },
  },
  toggle: {
    toGQLQuery(filterValue) {
      if (!filterValue) return undefined;

      return filterValue;
    },
    toURLParam(filterValue) {
      if (!filterValue) return undefined;

      return String(filterValue);
    },
    toFilterOptions(filterValue) {
      return Boolean(filterValue) ?? undefined;
    },
  },
  multiselect_flag: {
    toGQLQuery: filterValue => {
      const selected = filterValue.filter(pickSelectedFieldMapper);

      if (selected.length === filterValue.length) {
        return undefined;
      }

      const [selectedOption] = selected;

      return selectedOption.key;
    },
    toURLParam: filterValue => {
      const selected = filterValue.filter(pickSelectedFieldMapper);

      if (selected.length === filterValue.length) {
        return null;
      }

      return getFiltersQuery(selected, null);
    },
    toFilterOptions(filterValueFromQuery, defaultFilterValue) {
      if (!filterValueFromQuery) {
        return defaultFilterValue;
      }

      const activeFilters = getFilterValuesFromParam(filterValueFromQuery);
      return defaultFilterValue.map(setSelectedMapper(activeFilters));
    },
  },
  range: {
    toGQLQuery(filterValue) {
      if (!filterValue) return undefined;
      if (isNaN(Number(filterValue))) return undefined;

      return Number(filterValue);
    },
    toURLParam(filterValue) {
      return filterValue;
    },
    toFilterOptions(filterValue) {
      return filterValue ?? '';
    },
  },
};

const getGQLVariablesFromRangeFilterValues = (
  filtersValues: FilterValues,
  filterConfig: RangeFilter,
): GQLAttributes => {
  const filterValues = {
    [filterConfig.attributeMin]: filtersValues[filterConfig.attributeMin],
    [filterConfig.attributeMax]: filtersValues[filterConfig.attributeMax],
  };

  return Object.entries(filterValues).reduce((acc: GQLAttributes, [key, value]) => {
    // FIXME fix as, map value type to input type
    const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(value as string);

    if (filterGQLValue === undefined) return acc;

    const filterKey: string = toCamel(key);

    acc[filterKey] = filterGQLValue;

    return acc;
  }, {});
};

const getGQLVariablesFromInputFilterValues = (
  filtersValues: FilterValues,
  filterConfig: InputFilter,
): GQLAttributes => {
  const filter = filtersValues[filterConfig.key];

  const filterKey: string = toCamel(filterConfig.key);
  // FIXME fix as, map value type to input type
  const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(filter as string);

  if (filterGQLValue === undefined) return {};

  return { [filterKey]: filterGQLValue };
};

const getGQLVariablesFromToggleFilterValues = (
  filtersValues: FilterValues,
  filterConfig: ToggleFilter,
): GQLAttributes => {
  const filter = filtersValues[filterConfig.key];

  const filterKey: string = toCamel(filterConfig.key);

  const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(filter as boolean);

  if (filterGQLValue === undefined) return {};

  return { [filterKey]: filterGQLValue };
};

const getGQLVariablesFromSingleselectFilterValues = (
  filtersValues: FilterValues,
  filterConfig: SingleselectFilter,
): GQLAttributes => {
  const filter = filtersValues[filterConfig.key];

  const filterKey: string = toCamel(filterConfig.key);
  // FIXME fix as, map value type to input type
  const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(filter as FilterOption[]);

  if (filterGQLValue === undefined) return {};

  return { [filterKey]: filterGQLValue };
};

const getGQLVariablesFromTabsFilterValues = (filtersValues: FilterValues, filterConfig: TabsFilter): GQLAttributes => {
  const filter = filtersValues[filterConfig.key];

  const filterKey: string = toCamel(filterConfig.key);
  // FIXME fix as, map value type to input type
  const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(filter as FilterOption[]);

  if (filterGQLValue === undefined) return {};

  return { [filterKey]: filterGQLValue };
};

const getGQLVariablesFromMultiselectFilterValues = (
  filtersValues: FilterValues,
  filterConfig: MultiselectFilter,
): GQLAttributes => {
  const filter = filtersValues[filterConfig.key];

  const filterKey: string = toCamel(filterConfig.key);
  // FIXME fix as, map value type to input type
  const filterGQLValue = filterTransformator[filterConfig.type].toGQLQuery(filter as FilterOption[], filterConfig);

  if (filterGQLValue === undefined) return {};

  return { [filterKey]: filterGQLValue };
};

export const getGQLVariablesFromFilterValues = (
  filtersValues: FilterValues,
  filtersConfig: FiltersConfig,
): GQLAttributes => {
  return filtersConfig.reduce((acc: GQLAttributes, filterConfig) => {
    let filterValues: GQLAttributes = {};

    if (filterConfig?.type === 'range') {
      filterValues = getGQLVariablesFromRangeFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'input') {
      filterValues = getGQLVariablesFromInputFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'toggle') {
      filterValues = getGQLVariablesFromToggleFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'singleselect') {
      filterValues = getGQLVariablesFromSingleselectFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'tabs') {
      filterValues = getGQLVariablesFromTabsFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'multiselect') {
      filterValues = getGQLVariablesFromMultiselectFilterValues(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'multiselect_flag') {
      filterValues = getGQLVariablesFromMultiselectFilterValues(filtersValues, filterConfig);
    }

    Object.entries(filterValues).forEach(([key, value]) => {
      acc[key] = value;
    });

    return acc;
  }, {});
};

const getQueryFromConfigForRange = (filtersValues: FilterValues, filterConfig: RangeFilter): QueryAttributes => {
  const filterValues = {
    [filterConfig.attributeMin]: filtersValues[filterConfig.attributeMin],
    [filterConfig.attributeMax]: filtersValues[filterConfig.attributeMax],
  };

  return Object.entries(filterValues).reduce((acc: QueryAttributes, [key, value]) => {
    // FIXME fix as, map value type to input type
    const filterURLParam = filterTransformator[filterConfig.type].toURLParam(value as string);

    if (!filterURLParam) return acc;

    acc[key] = filterURLParam;

    return acc;
  }, {});
};

const getQueryFromConfigForInput = (filtersValues: FilterValues, filterConfig: InputFilter): QueryAttributes => {
  const filterValue = filtersValues[filterConfig.key];

  // FIXME fix as, map value type to input type
  const filterURLParam = filterTransformator[filterConfig.type].toURLParam(filterValue as string);

  if (!filterURLParam) return {};

  return { [filterConfig.key]: filterURLParam };
};

const getQueryFromConfigForToggle = (filtersValues: FilterValues, filterConfig: ToggleFilter): QueryAttributes => {
  const filterValue = filtersValues[filterConfig.key];

  const filterURLParam = filterTransformator[filterConfig.type].toURLParam(filterValue as boolean);

  if (!filterURLParam) return {};

  return { [filterConfig.key]: filterURLParam };
};

const getQueryFromConfigForSingleselect = (
  filtersValues: FilterValues,
  filterConfig: SingleselectFilter,
): QueryAttributes => {
  const filterValue = filtersValues[filterConfig.key];

  // FIXME fix as, map value type to input type
  const filterURLParam = filterTransformator[filterConfig.type].toURLParam(filterValue as FilterOption[], filterConfig);

  if (!filterURLParam) return {};

  return { [filterConfig.key]: filterURLParam };
};

const getQueryFromConfigForTabs = (filtersValues: FilterValues, filterConfig: TabsFilter): QueryAttributes => {
  const filterValue = filtersValues[filterConfig.key];

  // FIXME fix as, map value type to input type
  const filterURLParam = filterTransformator[filterConfig.type].toURLParam(filterValue as FilterOption[], filterConfig);

  if (!filterURLParam) return {};

  return { [filterConfig.key]: filterURLParam };
};

const getQueryFromConfigForMultiselect = (
  filtersValues: FilterValues,
  filterConfig: MultiselectFilter,
): QueryAttributes => {
  const filterValue = filtersValues[filterConfig.key];

  // FIXME fix as, map value type to input type
  const filterURLParam = filterTransformator[filterConfig.type].toURLParam(filterValue as FilterOption[]);

  if (!filterURLParam) return {};

  return { [filterConfig.key]: filterURLParam };
};

export const getQueryFromConfig = ({ filtersValues, filtersConfig }: GetQueryFuncPayload) => {
  return filtersConfig.reduce((acc: QueryAttributes, filterConfig) => {
    let values: QueryAttributes = {};

    if (filterConfig?.type === 'range') {
      values = getQueryFromConfigForRange(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'input') {
      values = getQueryFromConfigForInput(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'toggle') {
      values = getQueryFromConfigForToggle(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'singleselect') {
      values = getQueryFromConfigForSingleselect(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'tabs') {
      values = getQueryFromConfigForTabs(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'multiselect') {
      values = getQueryFromConfigForMultiselect(filtersValues, filterConfig);
    }

    if (filterConfig?.type === 'multiselect_flag') {
      values = getQueryFromConfigForMultiselect(filtersValues, filterConfig);
    }

    Object.entries(values).forEach(([key, value]) => {
      acc[key] = value;
    });

    return acc;
  }, {});
};

export const selectedOptionsToQueryParams = (options: MultiselectFilterSet): QueryParams => {
  return Object.keys(options).reduce((acc: QueryParams, key) => {
    acc[key] = getFiltersQuery(options[key].map(keyToFilterOptionMapper).map(setSelectedMapper()), null);

    return acc;
  }, {});
};
